/**
 * Componente de Cards de Status da DLQ
 * 
 * Exibe métricas principais: total na DLQ, distribuição por reason,
 * e tempo do item mais antigo.
 * 
 * @author Sistema de Flows v3.0 - DLQ
 */

import { Skull, Clock, RefreshCw, AlertTriangle, Zap, Timer } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDlqStats } from '@/hooks/useDlqMonitor';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Labels legíveis para cada tipo de reason
 */
const reasonLabels: Record<string, string> = {
  'max_attempts_exceeded': 'Max Tentativas',
  'max_node_retries_exceeded': 'Max Retries Nó',
  'flow_engine_error': 'Erro Flow Engine',
  'timeout': 'Timeout',
  'invalid_payload': 'Payload Inválido',
};

export function DlqStatusCards() {
  const { data: stats, isLoading } = useDlqStats();

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Calcular principais motivos
  const reasonCounts = stats?.byReason || {};
  const topReasons = Object.entries(reasonCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2);

  const cards = [
    {
      title: 'Total na DLQ',
      value: stats?.total || 0,
      icon: Skull,
      color: stats?.total && stats.total > 0 ? 'text-red-600' : 'text-green-600',
      bgColor: stats?.total && stats.total > 0 
        ? 'bg-red-100 dark:bg-red-900/20' 
        : 'bg-green-100 dark:bg-green-900/20',
    },
    {
      title: 'Max Tentativas',
      value: reasonCounts['max_attempts_exceeded'] || 0,
      icon: RefreshCw,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100 dark:bg-orange-900/20',
    },
    {
      title: 'Max Retries Nó',
      value: reasonCounts['max_node_retries_exceeded'] || 0,
      icon: Zap,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900/20',
    },
    {
      title: 'Item Mais Antigo',
      value: stats?.oldestAt 
        ? formatDistanceToNow(new Date(stats.oldestAt), { addSuffix: true, locale: ptBR })
        : 'N/A',
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {card.title}
                </p>
                <p className="text-2xl font-bold">{card.value}</p>
              </div>
              <div className={`p-3 rounded-full ${card.bgColor}`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
